import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentSerialsApi } from '../../api/documentSerials';
import type { DocumentSerialDto, DocumentSerialType, SequenceResetFrequency } from '../../types/documentSerial';
import { formatDate, today } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { useReportScreen } from '../../components/ScreenContext';

const SERIAL_TYPES: { value: DocumentSerialType; label: string }[] = [
  { value: 'SalesInvoice', label: 'Sales Invoice' },
  { value: 'PurchaseInvoice', label: 'Purchase Invoice' },
  { value: 'CashIncome', label: 'Cash Income' },
  { value: 'CashSpending', label: 'Cash Spending' },
  { value: 'BankOperation', label: 'Bank Operation' },
  { value: 'Offset', label: 'Offset' },
  { value: 'AdvanceReport', label: 'Advance Report' },
  { value: 'WageSheet', label: 'Wage Sheet' },
];

const RESET_FREQUENCIES: { value: SequenceResetFrequency; label: string }[] = [
  { value: 'Never', label: 'Never' },
  { value: 'Yearly', label: 'Yearly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Daily', label: 'Daily' },
];

const PATTERN_EXAMPLES = [
  { label: 'Simple (SF000001)', value: '' },
  { label: 'With year (SF2026-0001)', value: '{PREFIX}{YYYY}-{SEQ:4}' },
  { label: 'With year-month (SF202601-0001)', value: '{PREFIX}{YYYY}{MM}-{SEQ:4}' },
  { label: 'Full date (SF20260101-0001)', value: '{PREFIX}{YYYYMMDD}-{SEQ:4}' },
  { label: 'Yearly reset (2026/SF-0001)', value: '{YYYY}/{PREFIX}-{SEQ:4}' },
];

interface SerialForm {
  serialType: DocumentSerialType;
  serialCode: string;
  dateFrom: string;
  dateInNumber: boolean;
  pattern: string;
  sequencePadding: number;
  resetFrequency: SequenceResetFrequency;
  lastNumber: number;
  isObsolete: boolean;
}

const emptyForm = (): SerialForm => ({
  serialType: 'SalesInvoice',
  serialCode: '',
  dateFrom: today(),
  dateInNumber: false,
  pattern: '',
  sequencePadding: 6,
  resetFrequency: 'Never',
  lastNumber: 0,
  isObsolete: false,
});

export default function DocumentSerialManager() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingSerial, setEditingSerial] = useState<DocumentSerialDto | null>(null);
  const [form, setForm] = useState<SerialForm>(emptyForm());
  const [error, setError] = useState('');

  const { data: serials, isLoading } = useQuery({
    queryKey: ['document-serials'],
    queryFn: () => documentSerialsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: SerialForm) => documentSerialsApi.create({
      serialType: data.serialType,
      serialCode: data.serialCode,
      dateFrom: data.dateFrom,
      dateInNumber: data.dateInNumber,
      pattern: data.pattern || undefined,
      sequencePadding: data.sequencePadding,
      resetFrequency: data.resetFrequency,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-serials'] });
      closeForm();
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; form: SerialForm }) => documentSerialsApi.update(data.id, {
      serialCode: data.form.serialCode,
      lastNumber: data.form.lastNumber,
      dateInNumber: data.form.dateInNumber,
      isObsolete: data.form.isObsolete,
      pattern: data.form.pattern || undefined,
      sequencePadding: data.form.sequencePadding,
      resetFrequency: data.form.resetFrequency,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-serials'] });
      closeForm();
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => documentSerialsApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['document-serials'] });
      addToast(`Document serial #${id} deleted`, 'success');
    },
    onError: (err: Error) => setError(err.message),
  });

  const closeForm = () => {
    setShowForm(false);
    setEditingSerial(null);
    setForm(emptyForm());
    setError('');
  };

  const startEdit = (serial: DocumentSerialDto) => {
    setEditingSerial(serial);
    setForm({
      serialType: serial.serialType,
      serialCode: serial.serialCode,
      dateFrom: serial.dateFrom?.split('T')[0] ?? today(),
      dateInNumber: serial.dateInNumber,
      pattern: serial.pattern ?? '',
      sequencePadding: serial.sequencePadding,
      resetFrequency: (serial.resetFrequency as SequenceResetFrequency) || 'Never',
      lastNumber: serial.lastNumber,
      isObsolete: serial.isObsolete,
    });
    setShowForm(true);
  };

  const startCreate = () => {
    setEditingSerial(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const handleSubmit = () => {
    setError('');
    if (!form.serialCode.trim()) {
      setError('Serial code is required');
      return;
    }
    if (editingSerial) {
      updateMutation.mutate({ id: editingSerial.id, form });
    } else {
      createMutation.mutate(form);
    }
  };

  // Live preview of the next number
  const previewSample = useMemo(() => {
    if (!form.serialCode) return '';
    const nextNum = form.lastNumber + 1;
    const padding = form.sequencePadding || 6;
    const code = form.serialCode.toUpperCase();
    const d = new Date();

    if (form.pattern) {
      let result = form.pattern;
      result = result.replace(/\{PREFIX\}/gi, code);
      result = result.replace(/\{YYYY\}/gi, String(d.getFullYear()));
      result = result.replace(/\{YY\}/gi, String(d.getFullYear()).slice(-2));
      result = result.replace(/\{MM\}/gi, String(d.getMonth() + 1).padStart(2, '0'));
      result = result.replace(/\{DD\}/gi, String(d.getDate()).padStart(2, '0'));
      const seqMatch = result.match(/\{SEQ:(\d+)\}/i);
      if (seqMatch && seqMatch[1]) {
        result = result.replace(/\{SEQ:\d+\}/i, String(nextNum).padStart(parseInt(seqMatch[1], 10), '0'));
      } else {
        result = result.replace(/\{SEQ\}/gi, String(nextNum).padStart(padding, '0'));
      }
      return result;
    }

    if (form.dateInNumber) {
      return `${code}${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(nextNum).padStart(padding, '0')}`;
    }
    return `${code}${String(nextNum).padStart(padding, '0')}`;
  }, [form.serialCode, form.lastNumber, form.sequencePadding, form.pattern, form.dateInNumber]);

  const typeLabel = (t: string) => SERIAL_TYPES.find(s => s.value === t)?.label ?? t;

  // AI screen context (actions auto-discovered from semantic HTML)
  const serialCount = serials?.length ?? 0;
  useReportScreen(`Dokumentu serijos (${serialCount} seriju)`, {
    recordCount: serialCount,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Document Serials</h1>
        <button
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          onClick={startCreate}
        >
          + Add Serial
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900">
            {editingSerial ? `Edit Serial: ${editingSerial.serialCode}` : 'New Document Serial'}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {!editingSerial && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Document Type *</label>
                <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={form.serialType} onChange={(e) => setForm({ ...form, serialType: e.target.value as DocumentSerialType })}>
                  {SERIAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Serial Code (Prefix) *</label>
              <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={form.serialCode} onChange={(e) => setForm({ ...form, serialCode: e.target.value })}
                placeholder="e.g. SF, BO, KIO" />
            </div>
            {!editingSerial && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Valid From</label>
                <input type="date" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={form.dateFrom?.substring(0, 10) ?? ''} onChange={(e) => setForm({ ...form, dateFrom: e.target.value })} />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sequence Padding</label>
              <input type="number" min={1} max={20} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={form.sequencePadding} onChange={(e) => setForm({ ...form, sequencePadding: parseInt(e.target.value) || 6 })} />
              <p className="text-xs text-gray-400 mt-0.5">e.g. 4 = "0001", 6 = "000001"</p>
            </div>
            {editingSerial && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Last Number</label>
                <input type="number" min={0} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  value={form.lastNumber} onChange={(e) => setForm({ ...form, lastNumber: parseInt(e.target.value) || 0 })} />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reset Frequency</label>
              <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={form.resetFrequency} onChange={(e) => setForm({ ...form, resetFrequency: e.target.value as SequenceResetFrequency })}>
                {RESET_FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            {editingSerial && (
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.isObsolete}
                    onChange={(e) => setForm({ ...form, isObsolete: e.target.checked })} />
                  <span className="text-sm text-gray-700">Obsolete</span>
                </label>
              </div>
            )}
          </div>

          {/* Pattern configuration */}
          <div className="space-y-2">
            <label className="block text-xs text-gray-500 font-medium">Number Pattern</label>
            <div className="flex flex-wrap gap-2">
              {PATTERN_EXAMPLES.map(ex => (
                <button key={ex.value} type="button"
                  className={`px-2 py-1 text-xs rounded-md border ${
                    form.pattern === ex.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setForm({ ...form, pattern: ex.value })}
                >
                  {ex.label}
                </button>
              ))}
            </div>
            {form.pattern && (
              <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono"
                value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                placeholder="{PREFIX}-{YYYY}-{SEQ:4}" />
            )}
            <p className="text-xs text-gray-400">
              Tokens: {'{PREFIX}'} {'{YYYY}'} {'{YY}'} {'{MM}'} {'{DD}'} {'{SEQ}'} {'{SEQ:N}'}
            </p>
          </div>

          {/* Preview */}
          {previewSample && (
            <div className="bg-gray-50 rounded-md p-3">
              <span className="text-xs text-gray-500">Next number preview: </span>
              <span className="text-sm font-mono font-semibold text-gray-800">{previewSample}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editingSerial ? 'Update' : 'Create'}
            </button>
            <button className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={closeForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Serials table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">Type</th>
                <th className="text-left">Code</th>
                <th className="text-right">Last #</th>
                <th className="text-left">Pattern</th>
                <th className="text-center">Padding</th>
                <th className="text-left">Reset</th>
                <th className="text-left">Valid From</th>
                <th className="text-left">Preview</th>
                <th className="text-left">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {serials?.map((serial) => {
                const nextNum = serial.lastNumber + 1;
                const padding = serial.sequencePadding || 6;
                const code = serial.serialCode.toUpperCase();
                const d = new Date();
                let preview = `${code}${String(nextNum).padStart(padding, '0')}`;
                if (serial.pattern) {
                  preview = serial.pattern
                    .replace(/\{PREFIX\}/gi, code)
                    .replace(/\{YYYY\}/gi, String(d.getFullYear()))
                    .replace(/\{YY\}/gi, String(d.getFullYear()).slice(-2))
                    .replace(/\{MM\}/gi, String(d.getMonth() + 1).padStart(2, '0'))
                    .replace(/\{DD\}/gi, String(d.getDate()).padStart(2, '0'));
                  const seqMatch = preview.match(/\{SEQ:(\d+)\}/i);
                  if (seqMatch && seqMatch[1]) {
                    preview = preview.replace(/\{SEQ:\d+\}/i, String(nextNum).padStart(parseInt(seqMatch[1], 10), '0'));
                  } else {
                    preview = preview.replace(/\{SEQ\}/gi, String(nextNum).padStart(padding, '0'));
                  }
                } else if (serial.dateInNumber) {
                  preview = `${code}${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(nextNum).padStart(padding, '0')}`;
                }

                return (
                  <tr key={serial.id} className={`border-b border-gray-50 ${serial.isObsolete ? 'opacity-50' : ''}`}>
                    <td>{typeLabel(serial.serialType)}</td>
                    <td className="font-mono font-medium">{serial.serialCode}</td>
                    <td className="text-right">{serial.lastNumber}</td>
                    <td className="font-mono text-xs text-gray-500">{serial.pattern || '(simple)'}</td>
                    <td className="text-center">{serial.sequencePadding}</td>
                    <td>{serial.resetFrequency}</td>
                    <td className="whitespace-nowrap">{formatDate(serial.dateFrom)}</td>
                    <td className="font-mono text-xs font-semibold text-primary-700">{preview}</td>
                    <td>
                      <span className={`text-xs font-medium ${serial.isObsolete ? 'text-gray-400' : 'text-green-600'}`}>
                        {serial.isObsolete ? 'Obsolete' : 'Active'}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex gap-2 justify-end">
                        <button className="text-xs text-primary-600 hover:underline"
                          onClick={() => startEdit(serial)}>
                          Edit
                        </button>
                        <button className="text-xs text-red-500 hover:underline"
                          onClick={() => { if (confirm(`Delete serial "${serial.serialCode}"?`)) deleteMutation.mutate(serial.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!serials || serials.length === 0) && (
                <tr><td colSpan={10} className="text-center text-gray-400 py-4">No document serials configured. Click "+ Add Serial" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
